import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Globe3D from '../components/Globe3D';

const QUOTES = [
  { text: "All the world's a stage, and all the men and women merely players", author: "William Shakespeare" },
  { text: "I don't care what the newspapers say about me as long as they spell my name right", author: "P.T. Barnum" },
  { text: "The only thing worse than being talked about is not being talked about", author: "Oscar Wilde" },
  { text: "There is no such thing as bad publicity except your own obituary", author: "Brendan Behan" },
];

const SPLASH_DURATION_MS = 6500;
// 2026-07 UX audit — self-hosted (was upload.wikimedia.org: a blocked/slow
// Wikimedia CDN could hang the splash for the full 4s fallback).
const NASA_TEXTURE_URL = '/blue_marble_1280.jpg';

// Satellite image — comedy & tragedy masks (transparent PNG, downscaled to
// 192px — displayed at ≤86px, the old 784×1168 original was 616KB on the
// boot critical path).
const MASK_IMAGE_URL = '/masks_192.png';

// Configurable: orbiting mask satellites — 5 masks, increased 20%
const ORBITING_MASKS = [
  { orbitRadius: 230, speed: 6, startAngle: 0, tilt: 0, size: 78 },       // Equator (65 * 1.2)
  { orbitRadius: 220, speed: 9, startAngle: 72, tilt: 50, size: 66 },     // Near North Pole (55 * 1.2)
  { orbitRadius: 220, speed: 12, startAngle: 144, tilt: -50, size: 66 },  // Near South Pole (55 * 1.2)
  { orbitRadius: 225, speed: 8, startAngle: 216, tilt: 25, size: 72 },    // Mid North (60 * 1.2)
  { orbitRadius: 225, speed: 10, startAngle: 288, tilt: -25, size: 72 },  // Mid South (60 * 1.2)
];

// Globe base speed (slower than satellites)
const GLOBE_BASE_SPEED = 0.8;

function OrbitingMasks({ masks }) {
  const refs = useRef([]);

  // 2026-07 UX audit P2 — ONE shared rAF loop drives all 5 masks (was 5
  // independent loops, each forcing style writes per frame during boot).
  useEffect(() => {
    const startTime = performance.now();
    let raf;
    const animate = (now) => {
      const elapsed = (now - startTime) / 1000;
      for (let i = 0; i < masks.length; i++) {
        const el = refs.current[i];
        if (!el) continue;
        const mask = masks[i];
        const angle = (mask.startAngle - (elapsed / mask.speed) * 360) % 360; // Negative = reverse direction
        const rad = (angle * Math.PI) / 180;
        const tiltRad = (mask.tilt * Math.PI) / 180;

        // 3D position on orbit
        const x = Math.cos(rad) * mask.orbitRadius;
        const z = Math.sin(rad) * mask.orbitRadius;
        const y = Math.sin(rad) * Math.sin(tiltRad) * mask.orbitRadius * 0.5;

        // Smooth opacity: 1.0 at front (z = +max) → 0.1 at back (z = -max)
        const zNorm = (z + mask.orbitRadius) / (2 * mask.orbitRadius); // 0 (back) to 1 (front)
        el.style.transform = `translate(${x}px, ${y}px) scale(${0.6 + zNorm * 0.5})`;
        el.style.opacity = 0.1 + zNorm * 0.9;
        el.style.zIndex = z > 0 ? 3 : 1;
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [masks]);

  return masks.map((mask, i) => (
    <span
      key={i}
      ref={(el) => { refs.current[i] = el; }}
      className="absolute pointer-events-none"
      style={{
        left: '50%', top: '50%',
        marginLeft: -mask.size / 2,
        marginTop: -mask.size / 2,
        width: mask.size,
        height: mask.size,
        filter: 'drop-shadow(0 0 4px rgba(200,150,255,0.5))',
        transition: 'opacity 0.1s',
      }}
    >
      <img
        src={MASK_IMAGE_URL}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    </span>
  ));
}

export default function SplashScreen({ onComplete }) {
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [show, setShow] = useState(true);
  const [textureReady, setTextureReady] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setTextureReady(true);
    img.onerror = () => setTextureReady(true);
    img.src = NASA_TEXTURE_URL;
    const fallback = setTimeout(() => setTextureReady(true), 4000);
    return () => clearTimeout(fallback);
  }, []);

  useEffect(() => {
    if (!textureReady) return;
    const timer = setTimeout(() => {
      setShow(false);
      setTimeout(onComplete, 500);
    }, SPLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [textureReady, onComplete]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: '#020208' }}
          data-testid="splash-screen"
        >
          {/* Starmap background — Milky Way galaxy panorama, compressed to
              ~118KB (2026-07 UX audit: the 1.8MB 8K master competed with
              critical boot fetches on mobile networks; it's largely obscured
              by the vignette + glow overlays anyway). The `onError={hide}`
              handler is defensive — if any future file rename leaves
              a broken reference, the user gets a black background
              instead of the browser's "?" placeholder icon. */}
          <div className="absolute inset-0">
            <img
              src="/starmap_splash.jpg"
              alt=""
              className="w-full h-full"
              style={{
                objectFit: 'cover',
                objectPosition: 'center center',
                opacity: 0.85
              }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          {/* Dark vignette overlay to keep focus on globe */}
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 40%, transparent 20%, rgba(2,2,8,0.6) 70%)' }} />

          {/* Ambient galaxy glow */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at 30% 20%, rgba(20,25,50,0.2) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(25,15,40,0.15) 0%, transparent 40%)'
          }} />

          {/* Content — only after texture loaded */}
          {textureReady && (
            <div className="relative z-10 flex flex-col items-center px-8 text-center">
              {/* Globe with atmospheric glow */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8, type: 'spring' }}
                className="mb-8 relative flex items-center justify-center"
                style={{ width: 440, height: 440, perspective: '400px', transformStyle: 'preserve-3d' }}
              >
                {/* Single atmospheric glow layer — Ultra-thin bright limb, inverse quartic (1/r⁴) for extreme rapid decay */}
                <div className="absolute" style={{
                  width: 360, height: 360,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, transparent 0%, transparent 48%, rgba(210,245,255,0.88) 49.8%, rgba(195,238,252,0.72) 51%, rgba(178,230,248,0.58) 52.5%, rgba(160,220,242,0.45) 54%, rgba(142,208,235,0.34) 56%, rgba(122,195,228,0.25) 58.5%, rgba(102,180,218,0.18) 62%, rgba(82,162,208,0.12) 67%, rgba(62,142,195,0.078) 74%, rgba(45,122,180,0.048) 82%, rgba(30,102,165,0.026) 91%, transparent 100%)',
                  filter: 'blur(4px)',
                }} />
                {/* Globe — centered, smaller than glow */}
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <Globe3D size={280} spinning={true} spinSpeed={GLOBE_BASE_SPEED} brightness={0.9} baseSpeed={GLOBE_BASE_SPEED} opaque={true} />
                </div>
                
                {/* Orbiting 🎭 performing arts masks — JS animated for depth */}
                {textureReady && <OrbitingMasks masks={ORBITING_MASKS} />}
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="text-4xl font-bold tracking-tight mb-2 text-white -mt-12"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Fantasy World Star
              </motion.h1>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="text-sm text-white/60 mb-12"
              >
                Draft the world. Score the headlines.
              </motion.p>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="max-w-sm"
              >
                <p className="text-sm italic text-white/70 mb-2">
                  &ldquo;{quote.text}&rdquo;
                </p>
                <p className="text-xs text-white/40">
                  — {quote.author}
                </p>
              </motion.div>

              {/* v6.x — Group the "A Gazumba Games Production" credit and the
                  Gazumba Games logo into a single unit that fades in together
                  (per product: the last two splash elements should appear as
                  one group, not staggered). */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.0, duration: 0.8 }}
                className="flex flex-col items-center mt-10"
              >
                <p className="text-[10px] text-white/30">
                  © 2026 Gazumba Games. All Rights Reserved. Fantasy World Star™
                </p>
                <img
                  src="/gazumba-games-logo.png"
                  alt="Gazumba Games"
                  width={40}
                  height={40}
                  className="mt-2"
                  style={{
                    opacity: 0.7,
                    // Whiten dark greys so the logo reads on the near-black splash.
                    WebkitFilter: 'invert(1) drop-shadow(0 0 6px rgba(255,255,255,0.15))',
                    filter: 'invert(1) drop-shadow(0 0 6px rgba(255,255,255,0.15))',
                  }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid="splash-gazumba-logo"
                />
              </motion.div>
            </div>
          )}

          {/* Loading state */}
          {!textureReady && (
            <div className="relative z-10 flex flex-col items-center">
              <div className="h-12 w-12 rounded-full border-2 border-white/20 border-t-white/60 animate-spin mb-4" />
              <p className="text-sm text-white/40">Loading...</p>
            </div>
          )}

        </motion.div>
      )}
    </AnimatePresence>
  );
}
